import { engineComponent, StringUtils, ArrayUtils } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { BezierPlugin } from "gsap";
import { BaseElement } from "../Components/Elements/Graphics/BaseElement";
import { StageController } from "../Components/StageController";
import { AnimationStyle } from "../Components/Elements/Styles/AnimationStyle";
import { StyleManager } from "../Components/Managers/StyleManager";
import { Tweens } from "../Components/Effects/Tweens";
import { CustomUtils } from "../Utils/CustomUtils";

@engineComponent(ComponentTypes.DisplayObject)
export class RopeElement extends BaseElement {
    // private _rope: PIXI.mesh.Rope;
    private _rope: PIXI.mesh.Rope;
    private _texture: PIXI.Texture;
    private _pointsCount: number;
    private _bezierPoints: Array<PIXI.Point>;
    private _pointsData: Array<any>;
    private _pointsDataBuffer: Array<PIXI.Point>;
    private _blendMode: number;
    private _animationStyle: AnimationStyle;
    private _frame: number;
    private _tint: number;
    private _curviness: number;
    private _continue: boolean;
    private _isPlaying: boolean;
    private _randomStartFrame: boolean;
    private _time: number;
    private count: number;

    private _ropeLength: number;
    private _increment: number;
    private _parts: Array<number>;
    private _strength: Array<Array<number>>;
    private _smooth: Array<Array<number>>;

    private _created: boolean;
    private _noWebGL: boolean = false;

    constructor() {
        super();

        this._rope = null;
        this._pointsCount = 100;
        this._bezierPoints = [];
        this._pointsData = [];
        this._pointsDataBuffer = [];
        this._blendMode = 0;
        this._animationStyle = null;
        this._frame = 0;
        this._tint = null;
        this._curviness = 1;
        this._continue = false;
        this._isPlaying = false;
        this._randomStartFrame = false;
        this._time = -1;

        /**
         * Group of variables for wave effect
         */
        this.count = 0;
        this._ropeLength = 0;
        this._increment = 0;
        this._parts = [];
        this._strength = [];
        this._smooth = [];

        this._created = false;

        if (StageController.Renderer instanceof PIXI.CanvasRenderer) {
            this._noWebGL = true;
        }
    }

    public createByNode(node: Element): void {
        if (!this._created) {
            super.createByNode(node);

            if (node.hasAttribute(RopeElementAttributes.POINTS_COUNT)) {
                this._pointsCount = parseInt(node.getAttribute(RopeElementAttributes.POINTS_COUNT));
            }

            if (node.hasAttribute(RopeElementAttributes.BLEND_MODE)) {
                this._blendMode = CustomUtils.getBlendMode(
                    parseInt(node.getAttribute(RopeElementAttributes.BLEND_MODE)),
                );
            }

            if (node.hasAttribute(RopeElementAttributes.CURVINESS)) {
                this._curviness = parseFloat(node.getAttribute(RopeElementAttributes.CURVINESS));
            }

            if (node.hasAttribute(RopeElementAttributes.RANDOM_START_FRAME)) {
                this._randomStartFrame = StringUtils.parseBoolean(
                    node.getAttribute(RopeElementAttributes.RANDOM_START_FRAME),
                );
            }

            if (node.hasAttribute(RopeElementAttributes.TIME)) {
                this._randomStartFrame = false;
                this._time = parseFloat(node.getAttribute(RopeElementAttributes.TIME));
            }

            if (node.hasAttribute(RopeElementAttributes.TINT)) {
                if (node.getAttribute(RopeElementAttributes.TINT) === "0") {
                    this._tint = null;
                } else {
                    this._tint = parseInt(node.getAttribute(RopeElementAttributes.TINT), 16);
                }
            }

            if (node.hasAttribute(RopeElementAttributes.POINTS)) {
                let pointsData = this.formatPoints(node.getAttribute(RopeElementAttributes.POINTS));
                let bezierData = BezierPlugin.bezierThrough(pointsData);

                this._bezierPoints = this.getBezierStepPoints(bezierData, this._pointsCount);
            }

            if (node.hasAttribute(RopeElementAttributes.IMAGE)) {
                let textureName = node.getAttribute(RopeElementAttributes.IMAGE);
                if (textureName.indexOf("ANI_") === 0) {
                    this._animationStyle = <AnimationStyle>StyleManager.Styles[textureName];
                    if (this._animationStyle != null) {
                        textureName = this._animationStyle.animation + "0000";
                    }
                }

                this._texture = StageController.getTextureByResourceId(textureName);
                if (this._texture != null) {
                    if (this._bezierPoints.length == 0) {
                        for (let i: number = 0; i < this._pointsCount; i++) {
                            this._bezierPoints.push(
                                new PIXI.Point(Math.round(this._texture.width / (this._pointsCount - 1)) * i, 0),
                            );
                        }
                    }

                    this._rope = new PIXI.mesh.Rope(this._texture, this._bezierPoints);
                    if (this._noWebGL) {
                        this._rope.canvasPadding = 5;
                    }
                    this._rope.blendMode = CustomUtils.getBlendMode(this._blendMode);

                    if (this._tint != null) {
                        this._rope.tint = this._tint;
                    }

                    this.addChild(this._rope);
                }
            }

            this._created = true;
        }
    }

    /**
     * Add base points to buffer.
     * @param {Array<any>} points
     */
    public addPoints(points: Array<PIXI.Point>): void {
        this._pointsDataBuffer = this._pointsDataBuffer.concat(points);
    }

    /**
     * Get base points to buffer.
     */
    public getPoints(): Array<any> {
        return this._pointsData;
    }

    /**
     *
     * @param {Element} node
     */
    public updateByNode(node: Element): void {
        if (this._created) {
            super.updateByNode(node);

            if (node.hasAttribute(RopeElementAttributes.POINTS)) {
                let XMLpointsData: Array<PIXI.Point> = this.formatPoints(
                    node.getAttribute(RopeElementAttributes.POINTS),
                );
                this._pointsData = [];

                if (
                    node.hasAttribute(RopeElementAttributes.REVERSE_POINTS) &&
                    StringUtils.parseBoolean(node.getAttribute(RopeElementAttributes.REVERSE_POINTS))
                ) {
                    this._pointsData = XMLpointsData.concat(this._pointsDataBuffer).reverse();
                } else {
                    this._pointsData = this._pointsDataBuffer.concat(XMLpointsData);
                }
                this._pointsDataBuffer = [];
                this.updatePoints(this._pointsData);
            }

            if (node.hasAttribute(RopeElementAttributes.GLOBAL_POINTS)) {
                this._pointsData = this.formatPoints(node.getAttribute(RopeElementAttributes.POINTS), true);
                this.updatePoints(this._pointsData);
            }

            if (node.hasAttribute(RopeElementAttributes.TINT)) {
                if (this._rope != null) {
                    if (node.getAttribute(RopeElementAttributes.TINT) === "0") {
                        this._tint = null;
                    } else {
                        this._tint = parseInt(node.getAttribute(RopeElementAttributes.TINT), 16);
                    }
                    this._rope.tint = this._tint;
                }
            }

            if (node.hasAttribute(RopeElementAttributes.IMAGE)) {
                let textureName = node.getAttribute(RopeElementAttributes.IMAGE);
                if (textureName.indexOf("ANI_") === 0) {
                    this._animationStyle = <AnimationStyle>StyleManager.Styles[textureName];
                    if (this._animationStyle != null) {
                        textureName = this._animationStyle.animation + "0000";
                    }
                }

                this._texture = StageController.getTextureByResourceId(textureName);
                if (this._texture != null) {
                    if (this._rope != null) {
                        this._rope.texture = this._texture;
                    }
                }
            }

            if (node.hasAttribute(RopeElementAttributes.ROPE_LENGTH)) {
                this._ropeLength = parseInt(node.getAttribute(RopeElementAttributes.ROPE_LENGTH));
            }

            /**
             *  - : from left to right
             *  + : from right to left
             */
            if (node.hasAttribute(RopeElementAttributes.INCREMENT)) {
                this._increment = parseFloat(node.getAttribute(RopeElementAttributes.INCREMENT));
            }

            /**
             *  Parts of Image with different wave effect moving
             *  It depends on points of images
             */
            if (node.hasAttribute(RopeElementAttributes.PARTS)) {
                this._parts = ArrayUtils.numberArray(node.getAttribute(RopeElementAttributes.PARTS));
            }

            if (node.hasAttribute(RopeElementAttributes.STRENGTH)) {
                let strength = ArrayUtils.numberArray(node.getAttribute(RopeElementAttributes.STRENGTH));
                for (let i = 0; i < this._parts.length; i++) {
                    if (strength.length == 2) {
                        this._strength.push([strength[0], strength[1]]);
                    } else {
                        this._strength.push([strength[i * 2], strength[i * 2 + 1]]);
                    }
                }
            }

            if (node.hasAttribute(RopeElementAttributes.SMOOTH)) {
                let smooth = ArrayUtils.numberArray(node.getAttribute(RopeElementAttributes.SMOOTH));
                for (let i = 0; i < this._parts.length; i++) {
                    if (smooth.length == 2) {
                        this._smooth.push([smooth[0], smooth[1]]);
                    } else {
                        this._smooth.push([smooth[i * 2], smooth[i * 2 + 1]]);
                    }
                }
            }

            if (
                node.hasAttribute(RopeElementAttributes.CONTINUE) &&
                StringUtils.parseBoolean(node.getAttribute(RopeElementAttributes.CONTINUE))
            ) {
                this._continue = true;
            }

            if (
                node.hasAttribute(RopeElementAttributes.PLAY) &&
                StringUtils.parseBoolean(node.getAttribute(RopeElementAttributes.PLAY))
            ) {
                this.play();
            }

            if (
                node.hasAttribute(RopeElementAttributes.STOP) &&
                StringUtils.parseBoolean(node.getAttribute(RopeElementAttributes.STOP))
            ) {
                this.stop();
            }
        }
    }

    /**
     *
     */
    private play(): void {
        if (this._animationStyle != null) {
            if (!this._continue || !this._isPlaying) {
                Tweens.removeTweens(this, "_frame");
                this.count = 0;

                let tweenDuration = (this._animationStyle.endFrame + 1) / this._animationStyle.speed;
                let tl = Tweens.customTween(this, {
                    tween: "_frame",
                    tweenValues: [0, this._animationStyle.endFrame],
                    tweenDuration: [tweenDuration],
                    tweenEase: ["linear"],
                    tweenLoop: this._animationStyle.loop,
                    callbackType: "onUpdate",
                    callbackFunction: this.updateTexture,
                    callbackScope: this,
                    callbackParams: ["{self}"],
                });
                tl.pause();

                if (this._randomStartFrame) {
                    tl.time(Math.random() * (tweenDuration / 60));
                }

                if (this._time > 0) {
                    tl.time(this._time);
                }

                tl.play();
                this._isPlaying = true;
            }
        } else {
            Tweens.removeTweens(this, "rope");

            Tweens.customTween(this, {
                tween: "rope",
                tweenValues: [0, 0],
                tweenDuration: [120],
                tweenEase: ["linear"],
                tweenLoop: true,
                callbackType: "onUpdate",
                callbackFunction: this.updateTexture,
                callbackScope: this,
                callbackParams: ["{self}"],
            });

            this._isPlaying = true;
        }
    }

    /**
     *
     */
    public stop(): void {
        Tweens.removeTweens(this, "rope");
        this._isPlaying = false;
        this.count = 0;
    }

    public setSpeed(speed: number): void {
        if (this._animationStyle != null) {
            this._animationStyle.speed = speed;
        }
    }

    /**
     *
     * @param self
     */
    private updateTexture(self: any): void {
        if (this._rope != null) {
            this.count += this._increment;
            for (let i: number = 0; i < this._pointsCount; i++) {
                for (let j = 0; j < this._parts.length; j++) {
                    if (i > this._parts[j]) {
                        this._rope.points[i].y = Math.sin(i * this._smooth[j][0] + this.count) * this._strength[j][0];
                        this._rope.points[i].x =
                            i * this._ropeLength + Math.cos(i * this._smooth[j][1] + this.count) * this._strength[j][1];
                    }
                }
            }
        }

        if (this._animationStyle != null) {
            let currentFrame: number = Math.round(this._frame);

            if (currentFrame > self.previousValue || currentFrame == 0) {
                this._rope.texture = StageController.getTextureByResourceId(
                    this._animationStyle.animation + this.zerosPad(currentFrame, 4),
                );
            }
            self.previousValue = currentFrame;
        }
    }

    /**
     *
     * @param num
     * @param size
     */
    private zerosPad(num: number, size: number): string {
        let s = num + "";
        while (s.length < size) s = "0" + s;
        return s;
    }

    /**
     *
     * @param {Array<any>} points
     */
    private updateMeshPoints(points: Array<any>): void {
        if (this._rope != null) {
            for (let i: number = 0; i < this._pointsCount; i++) {
                this._rope.points[i].x = points[i].x;
                this._rope.points[i].y = points[i].y;
            }
        }
    }

    /**
     *
     * @param {Array<any>} points
     */
    public updatePoints(points: Array<any>): void {
        let bezierData = BezierPlugin.bezierThrough(points, this._curviness);
        this._bezierPoints = this.getBezierStepPoints(bezierData, this._pointsCount);

        this.updateMeshPoints(this._bezierPoints);
    }

    /**
     *
     * @param {string} points
     * @param {boolean} useGlobal
     * @returns {Array<PIXI.Point>}
     */
    private formatPoints(points: string, useGlobal: boolean = false): Array<PIXI.Point> {
        let result: Array<PIXI.Point> = [];
        let regex = /\[(.*?)\]/g;
        let data = points.match(regex);

        for (let i: number = 0; i < data.length; i++) {
            let pointArray = data[i].substring(1, data[i].length - 1).split(",");

            if (pointArray.length == 2) {
                let x = this.formatPoint(pointArray[0], "x", useGlobal);
                let y = this.formatPoint(pointArray[1], "y", useGlobal);

                result.push(new PIXI.Point(x, y));
            }
        }

        return result;
    }

    /**
     *
     * @param {string} value
     * @param {string} id
     * @param {boolean} useGlobal
     * @returns {number}
     */
    private formatPoint(value: string, id: string, useGlobal: boolean = false): number {
        let result: number;
        if (value == "c") {
            result = this[id];
        } else {
            if (useGlobal) {
                result = this[id] + (parseInt(value) - this.getGlobalPosition()[id]);
            } else {
                result = parseInt(value);
            }
        }

        return result;
    }

    /**
     *
     * @param data
     * @param {number} length
     * @returns {Array<PIXI.Point>}
     */
    private getBezierStepPoints(data: any, length: number): Array<PIXI.Point> {
        let segmentLength = length / data.x.length;
        let pointPerSegment: Array<number> = [];
        let bezierStepPoints: Array<PIXI.Point> = [];

        if (data.x.length > 1) {
            segmentLength = Math.round(segmentLength);
            let sum = 0;
            for (let i: number = 0; i < data.x.length - 1; i++) {
                pointPerSegment.push(segmentLength);
                sum += segmentLength;
            }
            pointPerSegment.push(length - sum);
        } else {
            pointPerSegment = [segmentLength];
        }

        let time = 0;
        for (let i: number = 0; i < data.x.length; i++) {
            for (let j: number = 0; j < pointPerSegment[i]; j++) {
                if (i + 1 < data.x.length) {
                    time = j / pointPerSegment[i];
                } else {
                    time = j / (pointPerSegment[i] - 1);
                }
                bezierStepPoints.push(this.bezier(time, data.x[i], data.y[i]));
            }
        }

        return bezierStepPoints;
    }

    /**
     *
     * @param {number} t
     * @param {IBezierData} dataX
     * @param {IBezierData} dataY
     * @returns {PIXI.Point}
     */
    private bezier(t: number, dataX: IBezierData, dataY: IBezierData): PIXI.Point {
        let cX = 3 * dataX.ba,
            bX = 3 * (dataX.c - dataX.b) - cX,
            aX = dataX.da - cX - bX;

        let cY = 3 * dataY.ba,
            bY = 3 * (dataY.c - dataY.b) - cY,
            aY = dataY.da - cY - bY;

        let x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + dataX.a;
        let y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + dataY.a;

        return new PIXI.Point(x, y);
    }

    /**
     *
     * @returns {RopeElement}
     */
    clone(): RopeElement {
        let element: RopeElement = new RopeElement();

        element._created = this._created;
        element.visible = this.visible;
        element.alpha = this.alpha;
        element.position.x = this.position.x;
        element.position.y = this.position.y;
        element.pivot.x = this.pivot.x;
        element.pivot.y = this.pivot.y;
        element.scale.x = this.scale.x;
        element.scale.y = this.scale.y;
        element.rotation = this.rotation;

        element._pointsCount = this._pointsCount;
        element._texture = this._texture;
        element._blendMode = this._blendMode;
        element._animationStyle = this._animationStyle;
        element._frame = this._frame;
        element._tint = this._tint;
        element._randomStartFrame = this._randomStartFrame;
        element._increment = this._increment;
        element._parts = this._parts;
        element._smooth = this._smooth;
        element._strength = this._strength;
        element._ropeLength = this._ropeLength;

        element._bezierPoints = [];
        for (let i = 0; i < this._bezierPoints.length; i++) {
            element._bezierPoints.push(new PIXI.Point(this._bezierPoints[i].x, this._bezierPoints[i].y));
        }

        element._rope = new PIXI.mesh.Rope(element._texture, element._bezierPoints);
        element._rope.canvasPadding = this._rope.canvasPadding;
        element._rope.blendMode = element._blendMode;

        element.addChild(element._rope);

        element.markAsCloned(); // must be called last before return!
        return element;
    }
}

export interface IBezierData {
    a: number;
    b: number;
    ba: number;
    c: number;
    ca: number;
    d: number;
    da: number;
}

const RopeElementAttributes = {
    /**
     * Blend mode of mesh object.
     * @property BlendMode
     * @default 0
     */
    BLEND_MODE: "BlendMode",
    /**
     * Bezier curviness.
     * @default 1
     */
    CURVINESS: "Curviness",
    /**
     * Start animation in random frame.
     * @default false
     */
    RANDOM_START_FRAME: "RandomStartFrame",
    /**
     * Start time of the first animation loop. Automatically set RandomStartFrame to false.
     * @default false
     */
    TIME: "Time",
    /**
     * Global points, pairs of X,Y coords for mesh.
     * @property Points
     * @default null
     */
    GLOBAL_POINTS: "GlobalPoints",
    ROPE_LENGTH: "RopeLength",
    INCREMENT: "Increment",
    PARTS: "Parts",
    STRENGTH: "Strength",
    SMOOTH: "Smooth",
    /**
     * Reference to image from resources.
     * @property Image
     * @default 0
     */
    IMAGE: "Image",
    /**
     * Play animation
     * @property Play
     * @default false
     */
    PLAY: "Play",
    /**
     * If true, animation will not be stopped and restarted, when PLAY is fired
     * @property Play
     * @default false
     */
    CONTINUE: "Continue",
    /**
     * Points, pairs of X,Y coords for mesh.
     * @property Points
     * @default null
     */
    POINTS: "Points",
    /**
     * Number of X,Y points for mesh. Higher make mesh detailed.
     * @property PointsCount
     * @default 100
     */
    POINTS_COUNT: "PointsCount",
    /**
     * Reverse collected input points.
     */
    REVERSE_POINTS: "ReversePoints",
    /**
     * Stop animation
     * @property Play
     * @default false
     */
    STOP: "Stop",
    /**
     * Tint of image. Hex color which colorize movie clip.
     * @property Tint
     * @default null
     */
    TINT: "Tint",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/RopeElement.ts
 **/